'use strict';
const {normalizeLedger} = require('./audit-ledger.cjs');
const fail = code => { throw Object.assign(new Error(code),{code}); };
const finite = n => ['number','string'].includes(typeof n) && String(n).trim() !== '' && Number.isFinite(Number(n));
const validDate = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && Number.isFinite(Date.parse(s)) && new Date(s).toISOString().slice(0,10)===s;
function proof(e, expected, method, requireYear=true) {
 if(!e || e.schema!=='audit-data/v1' || e.source!=='24so')fail('AUDIT_SOURCE_INCOMPLETE');
 if(!expected.identityId || e.identityId!==expected.identityId)fail('AUDIT_IDENTITY_MISMATCH');
 if(e.period?.year!==Number(expected.year) || e.period.monthFrom!==Number(expected.monthFrom) || e.period.monthTo!==Number(expected.monthTo))fail('AUDIT_PERIOD_MISMATCH');
 if(e.completeness?.complete!==true || e.completeness.terminal!==true || e.completeness.method!==method)fail('AUDIT_SOURCE_INCOMPLETE');
 if(requireYear || e.fiscalYear){
  const f=e.fiscalYear;
  const start=`${expected.year}-${String(expected.monthFrom).padStart(2,'0')}-01`;
  const end=new Date(Date.UTC(Number(expected.year),Number(expected.monthTo),1)).toISOString().slice(0,10);
  if(f?.status!=='verified' || !validDate(f.start) || !validDate(f.endExclusive) || f.start>start || f.endExclusive<end)fail('AUDIT_FISCAL_YEAR_UNVERIFIED');
 }
}
function amounts(data){
 const seen=new Set();
 for(const [rows,keys,lo,hi] of [[data.saldo,['ib','ch','ub'],1000,2999],[data.resultat,['sum'],3000,8999]]){
  if(!Array.isArray(rows))fail('AUDIT_SOURCE_INCOMPLETE');
  for(const r of rows){
   if(!r || !Number.isInteger(Number(r.n)) || r.n<lo || r.n>hi || seen.has(Number(r.n)) || keys.some(k=>!finite(r[k]))
    || (lo===3000 && !r.months) || (r.months!=null && (typeof r.months!=='object' || Array.isArray(r.months) || Object.values(r.months).some(v=>!finite(v))))
    || (r.sumYtd!=null && !finite(r.sumYtd)))fail('AUDIT_SOURCE_INCOMPLETE');
   seen.add(Number(r.n));
  }
 }
 if(data.resultatFullYear!=null)amounts({saldo:[],resultat:data.resultatFullYear});
}
const MONTHS=['Jan','Feb','Mar','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Des'];
// Check individual period movements, never their net across accounts or months.
// Opening balances (and movement outside a partial period) are not transactions.
const zero = value => finite(value) && Number(value) === 0;
function zeroMonths(months, period) {
 if(!months || typeof months!=='object' || Array.isArray(months))return false;
 return Object.entries(months).every(([month,value])=>{
  const index=MONTHS.indexOf(month)+1;
  return index>0 && finite(value) && (index<period.monthFrom || index>period.monthTo || zero(value));
 });
}
const snapshotProof = e => ({schema:e.schema,source:e.source,identityId:e.identityId,
 period:{...e.period},completeness:{...e.completeness},...(e.fiscalYear?{fiscalYear:{...e.fiscalYear}}:{})});
function comparativeArithmetic(data,expected){
 const cents=v=>{const n=Math.round(Number(v)*100);if(!Number.isSafeInteger(n))fail('AUDIT_COMPARATIVE_INVALID');return n;};
 const sum=(row,from,to)=>{
  if(!row.months||Object.keys(row.months).some(k=>!MONTHS.includes(k)))fail('AUDIT_COMPARATIVE_INVALID');
  return MONTHS.slice(from-1,to).reduce((total,m)=>total+cents(row.months[m]??0),0);
 };
 for(const r of data.saldo){const movement=sum(r,1,12);if(cents(r.ch)!==movement||cents(r.ib)+movement!==cents(r.ub))fail('AUDIT_COMPARATIVE_INVALID');}
 for(const r of data.resultat)if(cents(r.sum)!==sum(r,Number(expected.monthFrom),Number(expected.monthTo)))fail('AUDIT_COMPARATIVE_INVALID');
 for(const r of data.resultatFullYear||[])if(cents(r.sum)!==sum(r,1,12))fail('AUDIT_COMPARATIVE_INVALID');
}
function prepareAudit({source,data,lines,expected,meta={}}){
 const uncheckedControls=[];
 const auditMeta={...meta,source,sourceIdentity:String(expected?.identityId||'')};
 // Only this successful preparation may issue evidence; never retain caller proof.
 delete auditMeta.transactionEvidence;
 const unchecked=(id,reason)=>uncheckedControls.push({id,reason});
 try{
  if(source!=='24so')fail('AUDIT_SOURCE_UNSUPPORTED');
  for(const [r,method] of [[data,'GetAggregated'],[lines,'GetTransactions']]){
   proof(r?.auditEvidence,expected,method,method==='GetAggregated');
   if(r.identityId!==expected.identityId)fail('AUDIT_IDENTITY_MISMATCH');
   if(r.year!==Number(expected.year) || r.startMonth!==Number(expected.monthFrom) || r.endMonth!==Number(expected.monthTo))fail('AUDIT_PERIOD_MISMATCH');
  }
  amounts(data);
  if(!Array.isArray(lines.transactions) || !Number.isInteger(lines.count) || lines.count!==lines.transactions.length
   || lines.auditEvidence.completeness.sourceRowCount!==lines.count)fail('AUDIT_SOURCE_INCOMPLETE');
  let previousYear=null;
  try{
   const p=data.previousYear;
   if(!p || p.identityId!==expected.identityId || p.year!==Number(expected.year)-1 || p.startMonth!==Number(expected.monthFrom)
     || p.endMonth!==Number(expected.monthTo) || p.saldoEndMonth!==12)fail('AUDIT_COMPARATIVE_UNVERIFIED');
   proof(p.comparativeEvidence?.pnl,{...expected,year:Number(expected.year)-1},'GetAggregated');
   proof(p.comparativeEvidence?.saldo,{...expected,year:Number(expected.year)-1,monthFrom:1,monthTo:12},'GetAggregated');
   amounts(p); comparativeArithmetic(p,expected); previousYear=p;
  }catch{for(const id of ['yoy_ib_ub_continuity','yoy_equity_decline','yoy_debt_increase','prior_year_not_closed'])unchecked(id,'comparative_evidence_unavailable');}
  const normalized=normalizeLedger({...data,previousYear},lines,expected);
  const period={year:Number(expected.year),monthFrom:Number(expected.monthFrom),monthTo:Number(expected.monthTo)};
  auditMeta.period=period;
  // Normalization discards most balance monthly fields and unknown PNL months. Reject contradictory
  // monthly movement before that evidence can be lost, even when period net is zero.
  if(!lines.transactions.length && [...data.saldo,...data.resultat].some(r=>r.months!=null && !zeroMonths(r.months,period)))fail('AUDIT_SOURCE_INCOMPLETE');
  const hasValues=rows=>rows.some(r=>['ib','ch','ub','sum'].some(k=>r[k]!=null&&Number(r[k])!==0)||Object.values(r.months||{}).some(v=>Number(v)!==0));
  if(!lines.transactions.length && !hasValues(data.saldo) && !hasValues(data.resultat))return {status:'no_data',auditMeta,uncheckedControls,blockers:[]};
  if(meta.vatSubmission?.status!=='ok')unchecked('mva_period_check',meta.vatSubmission?.reason||'vat_evidence_unavailable');
  if(!['ok','empty'].includes(meta.brreg?.auditor?.status))unchecked('revisjonsplikt',meta.brreg?.auditor?.reason||'roles_unavailable');
  if(meta.brreg?.lifecycle?.status==='deleted')unchecked('company_lifecycle','current_deleted_entity_requires_review');
  auditMeta.uncheckedControls=uncheckedControls;
  auditMeta.sourceEvidence={status:'verified',source,sourceIdentity:expected.identityId,companyName:data.client,
   period:{...period}};
  auditMeta.transactionEvidence={status:'verified',count:lines.count,
   aggregated:snapshotProof(data.auditEvidence),transactions:snapshotProof(lines.auditEvidence)};
  return {status:uncheckedControls.length?'incomplete':'ready',input:{system:'24seven',...normalized,txError:null,auditMeta},auditMeta,uncheckedControls,blockers:[]};
 }catch(e){return {status:'blocked',auditMeta,uncheckedControls,blockers:[{code:e.code||'AUDIT_DATA_INVALID'}]};}
}
async function fetchAndPrepareAudit({fetchFn,source,request,expected,meta={}}){
 try{
  if(source!=='24so')fail('AUDIT_SOURCE_UNSUPPORTED');
  const data=await fetchFn('/audit-data',JSON.stringify(request));
  const lines=await fetchFn('/get-transactions',JSON.stringify({...request,auditContract:'audit-data/v1'}));
  return prepareAudit({source,data,lines,expected,meta});
 }catch(e){
  const auditMeta={...meta,source};delete auditMeta.transactionEvidence;
  return {status:'blocked',auditMeta,uncheckedControls:[],blockers:[{code:e.code||'AUDIT_SOURCE_UNAVAILABLE'}]};
 }
}
module.exports={prepareAudit,fetchAndPrepareAudit,proof};
